import { bearing, distance } from '@turf/turf';
import { setFollowCamera, setRiderPosition, setRideRoute } from './map.js';

const MAX_ACCURACY_METERS = 100;
const WATCH_TIMEOUT_MS = 20000;
const MAX_RETRY = 4;

/**
 * Live GPS tracking controller. Acquires the device position, centers the
 * map on the user, follows their movement, and draws the traveled
 * trajectory. No ride recording or persistence.
 */
export class RideController {
  constructor(onUpdate, onError) {
    this.onUpdate = onUpdate;
    this.onError = onError;
    this.watchId = null;
    this.wakeLock = null;
    this.orientationHeading = 0;
    this.followMode = true;
    this.state = 'stopped';
    this.points = [];
    this.lastPosition = null;
    this.speedKmh = 0;
    this.lastError = '';
    this.retryCount = 0;
    this.orientationHandler = (event) => this.handleOrientation(event);
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible' && this.state === 'tracking') this.acquireWakeLock();
    };
    window.addEventListener('deviceorientationabsolute', this.orientationHandler, true);
    window.addEventListener('deviceorientation', this.orientationHandler, true);
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  /** Start live GPS tracking and follow the user on the map. */
  async track() {
    if (!navigator.geolocation) throw new Error('Geolocation is unavailable in this browser.');
    if (this.state === 'tracking' || this.state === 'acquiring') return;
    this.points = [];
    this.speedKmh = 0;
    this.retryCount = 0;
    setRideRoute([]);
    await this.acquireWakeLock();
    this.state = 'acquiring';
    this.lastError = '';
    this.emit();
    this.acquireCoarseFix();
  }

  /**
   * Stage 1: fast coarse fix (network/WiFi location). Shows an approximate
   * position within seconds instead of waiting ~30s for a cold GPS lock.
   * Stage 2 (startWatch) refines with high-accuracy satellite GPS.
   */
  acquireCoarseFix() {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.retryCount = 0;
        this.handlePosition(position);
        if (this.state === 'acquiring') {
          this.state = 'tracking';
          this.startWatch();
          this.emit();
        }
      },
      (error) => this.handleGeoError(error),
      { enableHighAccuracy: false, maximumAge: 60000, timeout: 8000 }
    );
  }

  /** Stop GPS tracking and clear the trajectory. */
  async stop() {
    if (this.state === 'stopped') return;
    this.stopWatch();
    await this.releaseWakeLock();
    this.state = 'stopped';
    this.emit();
  }

  destroy() {
    this.stopWatch();
    this.releaseWakeLock();
    window.removeEventListener('deviceorientationabsolute', this.orientationHandler, true);
    window.removeEventListener('deviceorientation', this.orientationHandler, true);
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  setFollowMode(enabled) {
    this.followMode = Boolean(enabled);
    if (this.lastPosition) this.moveCamera(this.lastPosition);
    this.emit();
  }

  startWatch() {
    if (this.watchId !== null) return;
    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        this.retryCount = 0;
        if (this.state === 'acquiring') {
          this.state = 'tracking';
          this.emit();
        }
        this.handlePosition(position);
      },
      (error) => this.handleGeoError(error),
      { enableHighAccuracy: true, maximumAge: 3000, timeout: WATCH_TIMEOUT_MS }
    );
  }

  stopWatch() {
    if (this.watchId !== null) navigator.geolocation.clearWatch(this.watchId);
    this.watchId = null;
  }

  handlePosition(position) {
    const { latitude: lat, longitude: lng, accuracy, speed, heading } = position.coords;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const now = position.timestamp || Date.now();
    const gpsHeading = Number.isFinite(heading) && heading >= 0 ? heading : this.orientationHeading;
    const current = { lat, lng, accuracy, timestamp: now, heading: gpsHeading };
    const isFirstFix = !this.lastPosition;
    const derivedSpeed = Number.isFinite(speed) && speed >= 0 ? speed * 3.6 : 0;

    if (this.lastPosition && !current.heading) {
      const segment = distance([this.lastPosition.lng, this.lastPosition.lat], [lng, lat], { units: 'meters' });
      if (segment >= 2) current.heading = bearing([this.lastPosition.lng, this.lastPosition.lat], [lng, lat]);
    }

    current.speedKmh = Math.max(0, Math.min(500, derivedSpeed));
    this.speedKmh = current.speedKmh;
    this.lastPosition = current;
    this.lastError = '';
    setRiderPosition(lng, lat, current.heading || 0, accuracy);

    if (isFirstFix) {
      setFollowCamera(true, lng, lat, current.heading || 0, 16);
      this.onError?.('GPS locked — following your location.');
    } else {
      this.moveCamera(current);
    }

    if (accuracy <= MAX_ACCURACY_METERS) {
      this.points.push(current);
      setRideRoute(this.points.map((point) => [point.lng, point.lat]));
    }
    this.emit();
  }

  moveCamera(position) {
    setFollowCamera(this.followMode, position.lng, position.lat, position.heading || 0);
  }

  handleGeoError(error) {
    const messages = {
      1: 'Location permission denied. Tap the GPS pill, then enable location for this site in your browser settings.',
      2: 'GPS position unavailable. Move outdoors or check device location services.',
      3: 'GPS request timed out. Retrying…',
    };
    this.lastError = messages[error.code] || 'Unknown GPS error.';
    this.onError?.(this.lastError);

    if (error.code === 1) {
      // Permission denied — stop trying; user must re-enable in browser settings.
      this.state = 'error';
      this.stopWatch();
    } else if ((error.code === 2 || error.code === 3) && this.retryCount < MAX_RETRY) {
      this.retryCount++;
      const delay = error.code === 2 ? 3000 : 2000;
      setTimeout(() => {
        if (this.state === 'acquiring' || this.state === 'tracking') this.acquireCoarseFix();
      }, delay);
    }
    this.emit();
  }

  emit() {
    this.onUpdate?.({
      state: this.state,
      speedKmh: this.speedKmh,
      pointCount: this.points.length,
      accuracy: this.lastPosition?.accuracy ?? null,
      heading: this.lastPosition?.heading ?? this.orientationHeading,
      position: this.lastPosition,
      followMode: this.followMode,
      lastError: this.lastError,
    });
  }

  handleOrientation(event) {
    const heading = Number.isFinite(event.webkitCompassHeading)
      ? event.webkitCompassHeading
      : Number.isFinite(event.alpha) ? (360 - event.alpha) % 360 : null;
    if (heading !== null) this.orientationHeading = heading;
  }

  async acquireWakeLock() {
    if (!navigator.wakeLock?.request) return;
    try {
      this.wakeLock = await navigator.wakeLock.request('screen');
      this.wakeLock.addEventListener('release', () => { this.wakeLock = null; });
    } catch {
      this.onError?.('Screen wake lock unavailable; keep the device awake manually.');
    }
  }

  async releaseWakeLock() {
    try { await this.wakeLock?.release(); } catch { /* already released */ }
    this.wakeLock = null;
  }
}
